import React from 'react';
import { Link } from 'react-router-dom';
import { FaFileContract, FaPhone, FaEnvelope, FaListUl } from 'react-icons/fa';
import { CONTACT, phoneHref } from '../constants/contact';
import './PolicyPage.css';

const sections = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    content: (
      <>
        <p>By accessing or using <strong>nxautocare.online</strong> (the "Site"), operated by <strong>Nodex Global</strong> ("Company", "we", "us", or "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Site or services.</p>
        <p>These Terms apply to all visitors, customers, and others who access or use our Site. We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Site following the posting of changes constitutes acceptance of those changes.</p>
      </>
    ),
  },
  {
    id: 'products-orders',
    title: 'Products & Orders',
    content: (
      <>
        <p>NX Auto Care sells used, remanufactured, and aftermarket automotive parts. By placing an order through nxautocare.online, you acknowledge and agree to the following:</p>
        <h3>Order Acceptance</h3>
        <ul>
          <li>All orders are subject to acceptance and availability confirmation by Nodex Global</li>
          <li>We reserve the right to refuse or cancel any order for any reason, including pricing errors or suspected fraud</li>
          <li>An order confirmation email does not constitute final acceptance — shipment of the item constitutes acceptance</li>
        </ul>
        <h3>Pricing & Availability</h3>
        <ul>
          <li>All prices are listed in US Dollars and are subject to change without notice</li>
          <li>We make every effort to display accurate inventory, but availability cannot be guaranteed until shipment</li>
          <li>In the event of a pricing error, we will notify you and give you the option to confirm or cancel the order</li>
          <li>Sales tax is applied based on your shipping destination in accordance with applicable state laws</li>
        </ul>
        <h3>Vehicle Fitment</h3>
        <p>While we make every effort to verify part compatibility, fitment information is provided as a guide only. The customer is responsible for confirming that the part is correct for their specific vehicle. NX Auto Care is not liable for labor costs or consequential damages resulting from incorrect fitment.</p>
      </>
    ),
  },
  {
    id: 'payment',
    title: 'Payment Terms',
    content: (
      <>
        <p>We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) and other payment methods as indicated at checkout. All payments are processed securely through our PCI-compliant payment partners.</p>
        <ul>
          <li>Payment is due in full at the time of purchase</li>
          <li>We do not store your full payment card details on our servers</li>
          <li>Chargebacks or disputes must be reported to us first at <a href="mailto:billing@nxautocare.online">billing@nxautocare.online</a> before contacting your bank</li>
          <li>Trade account customers with net-30 terms are subject to a separate credit agreement</li>
        </ul>
      </>
    ),
  },
  {
    id: 'shipping',
    title: 'Shipping & Delivery',
    content: (
      <>
        <p>Nodex Global ships parts throughout the contiguous United States via FedEx, UPS, and USPS. Our shipping terms include:</p>
        <ul>
          <li>Orders placed before 2:00 PM EST Monday–Friday are typically dispatched the same day</li>
          <li>Standard delivery takes 2–5 business days depending on your location</li>
          <li>Expedited 1–2 day shipping is available at checkout for an additional fee</li>
          <li>Free standard shipping is available on qualifying orders over $100</li>
          <li>Risk of loss passes to the buyer upon delivery to the carrier. NX Auto Care is not responsible for carrier delays, lost shipments, or damage during transit once the item has shipped.</li>
          <li>Freight shipments (large engines, transmissions) require a valid delivery address and may require a signature</li>
          <li>We do not ship to PO boxes for large or freight items</li>
        </ul>
        <p>International shipping is not currently available through nxautocare.online.</p>
      </>
    ),
  },
  {
    id: 'returns',
    title: 'Returns & Refunds',
    content: (
      <>
        <p>We want you to be fully satisfied with your purchase. Our return policy is as follows:</p>
        <h3>Eligible Returns</h3>
        <ul>
          <li>Unused, uninstalled parts in original condition may be returned within <strong>30 days</strong> of delivery</li>
          <li>All returned items must include the original packaging and any accompanying documents</li>
          <li>A Return Merchandise Authorization (RMA) number must be obtained before sending any item back</li>
        </ul>
        <h3>Non-Returnable Items</h3>
        <ul>
          <li>Electrical components once installed or opened (alternators, starters, sensors)</li>
          <li>Parts that have been modified, damaged, or show signs of installation attempts</li>
          <li>Special-order or custom-sourced parts</li>
          <li>Parts purchased more than 30 days ago (covered under warranty only)</li>
        </ul>
        <h3>Refund Process</h3>
        <p>Once your returned item is received and inspected, we will notify you of approval or rejection within 5 business days. Approved refunds are processed to your original payment method within 7–10 business days. Shipping costs for returns are the customer's responsibility unless the return is due to our error or a defective part.</p>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    content: (
      <>
        <p>All content on nxautocare.online — including text, graphics, logos, images, and software — is the property of Nodex Global or its content suppliers and is protected by applicable intellectual property laws.</p>
        <ul>
          <li>You may not reproduce, distribute, or create derivative works from our content without written permission</li>
          <li>The NX Auto Care brand, logo, and associated marks are trademarks of Nodex Global</li>
          <li>OEM brand names and logos referenced on the site are property of their respective manufacturers and are used for identification purposes only</li>
          <li>User-submitted content (reviews, inquiries) grants us a non-exclusive license to use, display, and share such content in connection with our services</li>
        </ul>
      </>
    ),
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer of Warranties',
    content: (
      <>
        <p>THE SITE AND ALL PRODUCTS ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, Nodex Global DISCLAIMS ALL WARRANTIES INCLUDING:</p>
        <ul>
          <li>IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
          <li>WARRANTIES THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES</li>
          <li>WARRANTIES AS TO THE ACCURACY OR COMPLETENESS OF ANY CONTENT ON THE SITE</li>
        </ul>
        <p>Some states do not allow the exclusion of implied warranties, so the above exclusion may not apply to you. Our specific part warranty terms are governed by our Warranty Policy.</p>
      </>
    ),
  },
  {
    id: 'limitation-liability',
    title: 'Limitation of Liability',
    content: (
      <>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, Nodex Global SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:</p>
        <ul>
          <li>Loss of profits or revenue</li>
          <li>Labor costs for installation or removal of parts</li>
          <li>Vehicle downtime or rental car expenses</li>
          <li>Data loss or service interruption</li>
        </ul>
        <p>In all cases, our total liability to you shall not exceed the amount paid for the specific product(s) giving rise to the claim.</p>
      </>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing Law & Disputes',
    content: (
      <>
        <p>These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any dispute arising under these Terms shall be resolved through binding arbitration in accordance with the American Arbitration Association rules, unless you opt out within 30 days of first accepting these Terms.</p>
        <p>You agree that any claims must be brought individually and not as part of a class action. Nothing in this section prevents either party from seeking injunctive relief for imminent harm.</p>
      </>
    ),
  },
  {
    id: 'contact',
    title: 'Contact Information',
    content: (
      <>
        <p>If you have questions about these Terms of Service, please contact {CONTACT.company} at:</p>
        <ul>
          <li><strong>Company:</strong> {CONTACT.company}</li>
          <li><strong>Address:</strong> {CONTACT.address}</li>
          <li><strong>Website:</strong> {CONTACT.website}</li>
          <li><strong>Email:</strong> <a href="mailto:legal@nxautocare.online">legal@nxautocare.online</a></li>
          <li><strong>Phone:</strong> {CONTACT.phone}</li>
          <li><strong>Hours:</strong> Monday–Friday 8AM–8PM EST, Saturday 9AM–5PM EST</li>
        </ul>
      </>
    ),
  },
];

const TermsOfService = () => {
  return (
    <div className="policy-page">
      <div className="policy-hero">
        <div className="policy-hero-badge">
          <FaFileContract /> Legal
        </div>
        <h1>Terms and Conditions</h1>
        <div className="policy-hero-meta">
          <span>Nodex Global</span>
          <span>|</span>
          <span>nxautocare.online</span>
          <span>|</span>
          <span>Last Updated: May 15, 2026</span>
        </div>
      </div>

      <div className="policy-body">
        <div className="policy-toc">
          <h3><FaListUl /> Table of Contents</h3>
          <ol>
            {sections.map((s, i) => (
              <li key={i}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </div>

        {sections.map((s, i) => (
          <div className="policy-section" id={s.id} key={i}>
            <h2>
              <span className="section-num">{i + 1}</span>
              {s.title}
            </h2>
            {s.content}
          </div>
        ))}

        <div className="policy-contact-box">
          <h3>Need Help with Our Terms?</h3>
          <p>Our team at Nodex Global is happy to clarify any part of these Terms of Service.</p>
          <div className="policy-contact-links">
            <a href="mailto:legal@nxautocare.online" className="link-primary">
              <FaEnvelope /> legal@nxautocare.online
            </a>
            <a href={phoneHref} className="link-secondary">
              <FaPhone /> {CONTACT.phone}
            </a>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: '#a0aec0' }}>
          &copy; 2026 Nodex Global. All rights reserved. |{' '}
          <Link to="/privacy-policy" style={{ color: 'var(--accent-orange)' }}>Privacy Policy</Link> |{' '}
          <Link to="/refund-policy" style={{ color: 'var(--accent-orange)' }}>Refund Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default TermsOfService;
